/**
 * The File System Access enhancement (D1, the Chromium half).
 *
 * ★★ WHAT IT BUYS. After a page reload the bytes of a chosen file are gone, so the universal
 * baseline is "re-pick the file and we continue from the server's offset" (resumeStore.ts). On
 * browsers with the File System Access API we can instead persist a HANDLE to the file, and on
 * reload re-acquire it with a single permission click — no file dialog, no hunting through folders.
 *
 * ⚠⚠ IT IS AN ENHANCEMENT, NOT THE MECHANISM. Safari and Firefox have no File System Access API,
 * and **Mac Safari is a first-class target** (§0e). Everything here is behind `supportsFileHandles()`
 * and every path degrades to the re-pick flow. If this file were deleted the portal would still
 * resume correctly everywhere — that is the test of whether the layering is right.
 *
 * ⚠ A handle is stored in IndexedDB, NOT the file. Handles are structured-cloneable; the bytes are
 * not, and copying a multi-GB master into IndexedDB to survive a refresh would be absurd.
 *
 * ⚠ PERMISSION NEEDS A USER GESTURE. On reload a stored handle usually reports `prompt`, and
 * `requestPermission()` only succeeds from a click. So the UI must offer a button — which is why
 * this exposes `resumeWithHandle()` rather than trying to reattach silently on mount.
 */

/** Minimal shapes — the DOM lib does not ship these in every TS version we build against. */
interface FsPermissionDescriptor {
  mode?: 'read' | 'readwrite'
}
export interface FileSystemFileHandleLike {
  readonly name: string
  getFile(): Promise<File>
  queryPermission?(d?: FsPermissionDescriptor): Promise<PermissionState>
  requestPermission?(d?: FsPermissionDescriptor): Promise<PermissionState>
}
interface PickerWindow {
  showOpenFilePicker?(options?: { multiple?: boolean }): Promise<FileSystemFileHandleLike[]>
}
interface HandleDataTransferItem extends DataTransferItem {
  getAsFileSystemHandle?(): Promise<FileSystemFileHandleLike | null>
}

const DB_NAME = 'yoreflix-upload'
const STORE = 'handles'

/** Is the enhancement available at all? Everything else in this module assumes this was checked. */
export function supportsFileHandles(): boolean {
  return typeof window !== 'undefined' && typeof (window as PickerWindow).showOpenFilePicker === 'function'
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1)
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE)
      }
      req.onsuccess = () => resolve(req.result)
      // ⚠ Safari Private Browsing can refuse IndexedDB outright. Resolving null (rather than
      // rejecting) keeps every caller on the graceful path.
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

export async function rememberHandle(id: string, handle: FileSystemFileHandleLike): Promise<void> {
  const db = await openDb()
  if (!db) return
  try {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(handle, id)
  } catch {
    // Best-effort: losing the handle only costs the provider a re-pick.
  }
}

export async function recallHandle(id: string): Promise<FileSystemFileHandleLike | null> {
  const db = await openDb()
  if (!db) return null
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(id)
      req.onsuccess = () => resolve((req.result as FileSystemFileHandleLike | undefined) ?? null)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

export async function forgetHandle(id: string): Promise<void> {
  const db = await openDb()
  if (!db) return
  try {
    db.transaction(STORE, 'readwrite').objectStore(STORE).delete(id)
  } catch {
    /* best-effort */
  }
}

/** Pick files via the handle-aware picker. Returns [] if the user cancels. */
export async function pickFilesWithHandles(): Promise<{ file: File; handle: FileSystemFileHandleLike }[]> {
  const picker = (window as PickerWindow).showOpenFilePicker
  if (!picker) return []
  try {
    const handles = await picker({ multiple: true })
    const out: { file: File; handle: FileSystemFileHandleLike }[] = []
    for (const handle of handles) out.push({ file: await handle.getFile(), handle })
    return out
  } catch {
    // The user cancelling the dialog throws — not an error worth surfacing.
    return []
  }
}

/**
 * Handles for dropped items, where the browser offers them.
 *
 * ⚠ Must read `dataTransfer.items` SYNCHRONOUSLY — the DataTransfer is neutered as soon as the
 * drop handler yields, so collecting the promises first and awaiting after is not optional.
 */
export function handlesFromDrop(dataTransfer: DataTransfer): Promise<(FileSystemFileHandleLike | null)[]> {
  const pending: Promise<FileSystemFileHandleLike | null>[] = []
  for (const item of Array.from(dataTransfer.items)) {
    const withHandle = item as HandleDataTransferItem
    if (item.kind === 'file' && typeof withHandle.getAsFileSystemHandle === 'function') {
      pending.push(withHandle.getAsFileSystemHandle().catch(() => null))
    }
  }
  return Promise.all(pending)
}

/**
 * Re-acquire a stored handle and read the file again — MUST be called from a user gesture.
 *
 * Returns the File on success, or null when the handle is gone, permission was refused, or the
 * file has been moved/deleted since. Every one of those falls back to the re-pick flow, which is
 * why the caller only ever treats null as "ask them to choose it".
 */
export async function resumeWithHandle(id: string): Promise<File | null> {
  const handle = await recallHandle(id)
  if (!handle) return null

  try {
    const query = (await handle.queryPermission?.({ mode: 'read' })) ?? 'granted'
    if (query !== 'granted') {
      const granted = (await handle.requestPermission?.({ mode: 'read' })) ?? 'denied'
      if (granted !== 'granted') return null
    }
    return await handle.getFile()
  } catch {
    // The file may have been renamed, moved or deleted while we were away.
    return null
  }
}
